import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, ArrowUpRight, ArrowDownLeft, CreditCard, TrendingUp, TrendingDown } from 'lucide-react';
import { Transaction } from '../App';

interface TokenDetailsProps {
  token: {
    symbol: string;
    name: string;
    balance: number;
    value: number;
    change: number;
    icon: string;
  };
  transactions: Transaction[];
  onBack: () => void;
  onSend: () => void;
  onReceive: () => void;
  onBuy: () => void;
}

export function TokenDetails({ token, transactions, onBack, onSend, onReceive, onBuy }: TokenDetailsProps) {
  console.log('🎨 TokenDetails - 전달받은 transactions:', transactions?.length || 0);
  console.log('🎨 TokenDetails - transactions 배열:', Array.isArray(transactions));
  
  // 블록체인 조회 데이터는 모두 VTTN 거래이므로 필터링 불필요
  const tokenTransactions = transactions || [];

  return (
    <div className="h-full bg-background flex flex-col">
      <div className="flex-1 overflow-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={onBack}
            className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-muted-foreground" />
          </button>
          <div className="text-center">
            <h1 className="text-xl font-bold text-foreground">{token.name}</h1>
            <p className="text-muted-foreground text-sm">{token.symbol}</p>
          </div>
          <div className="w-10 h-10"></div>
        </div>

        {/* Token Balance Card */}
        <Card className="bg-gradient-to-br from-blue-600 to-blue-500 border-0 text-white">
          <CardContent className="p-6 text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">
              {token.icon}
            </div>
            <div className="mb-4">
              <p className="text-3xl font-light mb-2">{token.balance.toFixed(2)}</p>
              <p className="text-white/80 text-lg">{token.symbol}</p>
            </div>
            <div className="flex items-center justify-center space-x-2 mb-2">
              <p className="text-2xl font-bold">${token.value.toFixed(2)}</p>
              {token.change >= 0 ? (
                <TrendingUp className="w-5 h-5 text-green-400" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-400" />
              )}
            </div>
            <p className={`text-sm ${token.change >= 0 ? 'text-green-300' : 'text-red-300'}`}>
              {token.change >= 0 ? '+' : ''}{token.change.toFixed(2)}% Today
            </p>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={onSend}
            className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 rounded-2xl flex flex-col items-center space-y-2 hover:scale-105 transition-transform shadow-lg"
          >
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <ArrowUpRight className="h-6 w-6" />
            </div>
            <span className="font-medium">Send</span>
          </button>

          <button
            onClick={onReceive}
            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-4 rounded-2xl flex flex-col items-center space-y-2 hover:scale-105 transition-transform shadow-lg"
          >
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <ArrowDownLeft className="h-6 w-6" />
            </div>
            <span className="font-medium">Receive</span>
          </button>

          <button
            onClick={onBuy}
            className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-4 rounded-2xl flex flex-col items-center space-y-2 hover:scale-105 transition-transform shadow-lg"
          >
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <CreditCard className="h-6 w-6" />
            </div>
            <span className="font-medium">Buy</span>
          </button>
        </div>

        {/* Transaction History */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-foreground">Transaction History</h3>
              <span className="text-muted-foreground text-sm">{tokenTransactions.length} transactions</span>
            </div>

            {tokenTransactions.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">{token.icon}</span>
                </div>
                <p className="text-muted-foreground">No {token.symbol} transactions yet</p>
                <p className="text-muted-foreground/60 text-sm mt-1">Your {token.symbol} activity will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-green-500 mb-2">✅ 거래 {tokenTransactions.length}건</p>
                {tokenTransactions.map((tx, index) => {
                  // 블록체인 데이터 사용 (hash, from, to, value, timestamp)
                  const isReceived = tx.to === tx.to; // 일단 모든 거래 표시
                  const amount = parseFloat(tx.value || tx.amount?.toString() || '0');
                  const txHash = tx.hash || `tx-${tx.id}`;
                  
                  return (
                    <div key={txHash + index} className="flex items-center justify-between p-3 bg-muted rounded-xl hover:bg-accent transition-colors cursor-pointer">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          amount > 0 ? 'bg-green-500/20' : 'bg-red-500/20'
                        }`}>
                          {amount > 0 ? (
                            <ArrowDownLeft className="h-5 w-5 text-green-600" />
                          ) : (
                            <ArrowUpRight className="h-5 w-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-foreground font-medium">
                            {tx.from?.substring(0, 10)}... → {tx.to?.substring(0, 10)}...
                          </p>
                          <p className="text-muted-foreground text-sm">
                            {tx.timestamp 
                              ? new Date(tx.timestamp * 1000).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : new Date(tx.createdAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                            }
                          </p>
                          <p className="text-xs text-blue-500">블록 #{tx.blockNumber || 'Pending'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${
                          amount > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {amount > 0 ? '+' : ''}{amount.toFixed(2)} {token.symbol}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          ≈ ${Math.abs(amount * (token.value / token.balance)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}